'use client'

import { Button } from '@/components/ui/button'
import { Footer } from '@/components/footer'
import { OorvaLogo } from '@/components/ui/oorva-logo'
import {
  allCanonicalExperienceSlugs,
  getCanonicalEditorial,
  getCanonicalExperiencePath,
  getCanonicalExperienceSlug,
  getDiscoveryByCanonical,
} from '@/lib/canonical-experiences'
import { getHomepageExperiencePath } from '@/lib/homepage-experiences'
import { liveDistricts } from '@/lib/live-districts'
import { ExperienceViewTracker } from '@/components/analytics/experience-view-tracker'
import { PreBookDialog } from '@/components/forms/pre-book-dialog'
import {
  ArrowLeft,
  ArrowRight,
  Clock3,
  IndianRupee,
  MapPin,
  MessageCircle,
  Users,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useParams, useRouter } from 'next/navigation'

/**
 * The single experience detail template.
 *
 * Layout follows the discover design (top title block, fact grid, hero image
 * with pre-book, step-by-step sections, pre-book card). When an editorial
 * record exists for the slug, its rich details (hook, host story, what
 * happens, conversation card, good-to-know) fill that layout. Catalogue-only
 * experiences render their catalogue copy in the same layout.
 */

const themeLabels = {
  craft: 'Create with Artisans',
  food: 'Cook with Locals',
  land: 'Walk the Farms',
  heritage: 'Experience Folk Arts',
  music: 'Celebrate Traditions',
} as const

function hasRealHotspots(
  hotspots: Array<[string, string]>,
  location: string,
  district: string,
) {
  if (hotspots.length !== 2) return hotspots.length > 0
  const [[a, aLabel], [b, bLabel]] = hotspots
  const isDefault =
    a.trim() === location.trim() &&
    aLabel === 'Destination' &&
    b === district &&
    bLabel === 'District'
  return !isDefault
}

export function FeaturedExperiencePage({ slug: slugProp }: { slug?: string } = {}) {
  const params = useParams<{ slug: string }>()
  const router = useRouter()
  const paramSlug = Array.isArray(params.slug) ? params.slug[0] : params.slug
  const slug = slugProp ?? paramSlug
  const canonical = slug ? getCanonicalExperienceSlug(slug) : undefined

  const editorial = canonical ? getCanonicalEditorial(canonical) : undefined
  const discovery = canonical ? getDiscoveryByCanonical(canonical) : undefined

  if (!canonical || (!editorial && !discovery)) {
    return <MissingExperience />
  }

  const catalogue = allCanonicalExperienceSlugs()
  const nextSlug = catalogue[(catalogue.indexOf(canonical) + 1) % catalogue.length]
  const nextEditorial = getCanonicalEditorial(nextSlug)
  const nextDiscovery = nextEditorial ? undefined : getDiscoveryByCanonical(nextSlug)
  const nextName =
    nextEditorial?.name ?? nextDiscovery?.title.replace('\n', ' ') ?? nextSlug

  // Blended content: editorial details win when they exist, catalogue fills
  // the gaps (and fully drives catalogue-only experiences).
  const experienceName = editorial?.name ?? discovery!.title.replace('\n', ' ')
  const headline = editorial?.title ?? discovery!.title
  const eyebrow =
    editorial?.craftLine ??
    `${themeLabels[discovery!.theme]} · ${discovery!.district}`
  const location = editorial?.location ?? discovery!.location
  const destination = editorial?.district ?? discovery!.district
  const bookingOpen = liveDistricts.includes(destination)
  const duration = editorial?.duration ?? discovery!.duration
  const hostName = editorial?.hostName ?? discovery!.hostName ?? 'Hosted locally'
  const image = editorial?.image ?? discovery!.image
  const heroLine = editorial?.instagramHook ?? discovery!.oneLine
  const price = editorial?.price

  const related = catalogue
    .filter((slug) => slug !== canonical)
    .map((slug) => {
      const ed = getCanonicalEditorial(slug)
      const di = ed ? undefined : getDiscoveryByCanonical(slug)
      return {
        slug,
        name: ed?.name ?? di?.title.replace('\n', ' ') ?? slug,
        location: ed?.location ?? di?.location ?? '',
        district: ed?.district ?? di?.district ?? '',
      }
    })
    .filter((item) => item.district === destination)
    .slice(0, 3)

  const returnToExperiences = () => {
    if (typeof document !== 'undefined' && document.referrer.startsWith(window.location.origin)) {
      try {
        const from = new URL(document.referrer).pathname
        // Prefer in-app back when history exists, but never strand users on
        // a dead back — fall through to an explicit catalogue route.
        if (window.history.length > 1) {
          router.back()
          return
        }
        if (from.startsWith('/destinations') || from.startsWith('/thanjavur')) {
          router.push(from)
          return
        }
      } catch {
        // fall through to default
      }
      router.back()
      return
    }
    router.push('/experiences')
  }

  const preBookTrigger = (label: string, className?: string) => bookingOpen ? (
    <PreBookDialog
      experienceName={experienceName}
      experienceSlug={canonical}
      location={location}
      trigger={
        <Button type="button" size="xl" className={className ?? 'w-full bg-brand text-white hover:bg-brand-hover focus-visible:border-brand focus-visible:ring-brand/30 sm:w-auto'}>
          <MessageCircle data-icon="inline-start" />
          {label}
        </Button>
      }
    />
  ) : (
    <a href={`mailto:hello@oorva.in?subject=${encodeURIComponent(`Notify me when ${experienceName} opens`)}`} className="site-button-primary text-center">Ask to be notified</a>
  )

  return (
    <div className="oorva-site-theme min-h-screen overflow-hidden bg-background text-foreground selection:bg-brand selection:text-white">
      <ExperienceViewTracker
        experienceSlug={canonical}
        experienceName={experienceName}
        destination={destination}
      />
      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8 xl:px-10">
        <header className="flex items-center justify-between">
          <Link href="/" aria-label="Oorva home" className="inline-flex min-h-11 items-center"><OorvaLogo size="lg" /></Link>
          <button type="button" onClick={returnToExperiences} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-card px-4 text-xs font-semibold text-muted-foreground transition hover:border-foreground/25 hover:text-foreground"><ArrowLeft className="size-3.5" />Back</button>
        </header>

        <main>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 pt-12 text-xs text-muted-foreground sm:pt-16">
            <Link href="/" className="transition hover:text-foreground">Oorva</Link>
            <span aria-hidden="true">/</span>
            <Link href="/experiences" className="transition hover:text-foreground">Experiences</Link>
            <span aria-hidden="true">/</span>
            <Link href="/destinations" className="transition hover:text-foreground">{destination}</Link>
            <span aria-hidden="true">/</span>
            <span className="font-semibold text-foreground" aria-current="page">{experienceName}</span>
          </nav>
          {/* ── Top section: eyebrow + headline ── */}
          <section className="pb-8 pt-6 sm:pt-8 lg:pb-10">
            <div className="max-w-5xl">
              <p className="site-label">{eyebrow}</p>
              <h1 className="mt-6 whitespace-pre-line font-sans text-[clamp(2.6rem,7vw,6.4rem)] font-semibold leading-[0.98] tracking-[-0.05em] sm:mt-7">{headline}</h1>
            </div>
          </section>

          {/* ── Hero image (clean, no pre-book overlay) ── */}
          <section className="relative min-h-[clamp(440px,66svh,700px)] overflow-hidden rounded-[1.5rem] bg-primary text-white">
            <Image src={image} alt={`${experienceName} in ${location}, ${destination} — an Oorva experience hosted by locals`} fill priority className="object-cover brightness-[0.82] saturate-[0.82]" sizes="100vw" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/90 via-transparent to-primary-dark/10" />
            <div className="absolute inset-x-5 bottom-6 border-t border-white/35 pt-5 sm:inset-x-8 sm:bottom-8 lg:inset-x-10 lg:bottom-10 xl:inset-x-12">
              <p className="max-w-2xl font-sans text-xl font-medium leading-tight sm:text-3xl">{heroLine}</p>
            </div>
          </section>

          {/* ── Facts: time + details below the hero ── */}
          <section className="pt-6 sm:pt-8">
            {!bookingOpen && <p className="mb-4 rounded-xl border border-brand/25 bg-brand-soft p-4 text-sm font-medium">Opening next in {destination}. Booking dates and prices are not available yet.</p>}
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border bg-border lg:grid-cols-4">
              <Fact icon={MapPin} label="Location" value={location} />
              <Fact icon={Clock3} label="Duration" value={duration} />
              <Fact icon={Users} label="Host" value={hostName} />
              {price ? (
                <Fact icon={IndianRupee} label="Price" value={price} />
              ) : (
                <Fact icon={Clock3} label="Booking" value={bookingOpen ? 'Price and group size confirmed before booking' : 'Opening next'} />
              )}
            </div>
          </section>

          {/* ── The hook ── */}
          {editorial ? (
            <section className="border-b border-border py-14 sm:py-20">
              <p className="site-label">The hook</p>
              <p className="mt-5 max-w-3xl font-sans text-3xl font-semibold leading-tight text-foreground sm:text-4xl">{editorial.hook}</p>
            </section>
          ) : (
            <section className="border-b border-border py-14 sm:py-20">
              <p className="site-label">What stays with you</p>
              <p className="mt-5 max-w-3xl font-sans text-3xl font-semibold leading-tight text-foreground sm:text-4xl">{discovery!.staysWithYou}</p>
            </section>
          )}

          {/* ── The host ── */}
          {editorial && (
            <section className="grid gap-8 border-b border-border py-14 sm:py-20 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
              <div>
                <p className="site-label">Hosted by locals</p>
                <h2 className="mt-5 max-w-xl font-sans text-4xl font-semibold leading-[0.98] tracking-[-0.04em] sm:mt-6 sm:text-5xl">{editorial.hostTitle}</h2>
              </div>
              <div className="grid gap-6 sm:grid-cols-[minmax(0,280px)_1fr] sm:gap-8">
                <div className="relative aspect-[4/5] overflow-hidden rounded-[1.5rem] bg-secondary">
                  <Image
                    src={editorial.hostPhoto}
                    alt={editorial.hostName}
                    fill
                    className="object-cover"
                    sizes="(min-width: 1024px) 280px, 60vw"
                  />
                </div>
                <p className="self-center text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">{editorial.hostBio}</p>
              </div>
            </section>
          )}

          {/* ── What you will do / what happens ── */}
          <section className="grid gap-8 border-b border-border py-14 sm:py-20 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
            <div>
              <p className="site-label">What you will do</p>
              <h2 className="mt-5 max-w-xl font-sans text-4xl font-semibold leading-[0.98] tracking-[-0.04em] sm:mt-6 sm:text-5xl lg:text-6xl">A real experience, not a performance.</h2>
              {editorial && (
                <div className="mt-8 space-y-4">
                  {(
                    [
                      { label: 'Storytelling', hint: 'The history, the meaning, the why.', score: editorial.dimensions.storytelling },
                      { label: 'Hands-on', hint: 'How much you do, not just watch.', score: editorial.dimensions.handsOn },
                      { label: 'Hospitality', hint: 'Warmth, tea, time with the host.', score: editorial.dimensions.hospitality },
                    ] as const
                  ).map(({ label, hint, score }) => (
                    <div key={label} className="space-y-2">
                      <DimensionMeter score={score} />
                      <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">{label}.</span> {hint}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <ol className="border-t border-border">
              {(editorial ? editorial.whatHappens : discovery!.description).map((step, index) => (
                <li key={step.slice(0, 32)} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-4 border-b border-border py-6 sm:grid-cols-[3rem_minmax(0,1fr)] sm:gap-5 sm:py-7">
                  <span className="text-xs font-semibold text-accent-foreground">0{index + 1}</span>
                  <p className="text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">{step}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* ── Good to know ── */}
          {editorial && (
            <section className="grid gap-8 border-b border-border py-14 sm:py-20 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
              <div>
                <p className="site-label">Good to know</p>
                <h2 className="mt-5 font-sans text-4xl font-semibold sm:mt-6 sm:text-5xl">The honest details, before you come.</h2>
              </div>
              <div className="border-t border-border">
                {editorial.goodToKnow.map((item) => (
                  <div key={item.label} className="grid gap-1.5 border-b border-border py-5 sm:grid-cols-[200px_1fr] sm:gap-8 sm:py-6">
                    <p className="font-sans text-lg font-semibold tracking-[-0.015em] text-foreground">{item.label}</p>
                    <p className="leading-7 text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── Nearby places (only when the catalogue has real ones) ── */}
          {discovery && hasRealHotspots(discovery.hotspots, discovery.location, discovery.district) && (
            <section className="grid gap-8 border-b border-border py-14 sm:py-20 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
              <div>
                <p className="site-label">Make a day of it</p>
                <h2 className="mt-5 font-sans text-4xl font-semibold sm:mt-6 sm:text-5xl">Nearby places</h2>
                <p className="mt-4 max-w-md leading-7 text-muted-foreground">These are useful ideas around the experience location. Confirm travel time and opening hours while planning your visit.</p>
              </div>
              <div className="border-t border-border">
                {discovery.hotspots.map(([name, proximity], index) => (
                  <div key={name} className="grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 border-b border-border py-5 sm:grid-cols-[3rem_minmax(0,1fr)_auto] sm:gap-4 sm:py-6">
                    <span className="text-xs font-semibold text-accent-foreground">0{index + 1}</span>
                    <span className="inline-flex min-w-0 items-center gap-3 text-base sm:text-lg"><MapPin className="size-4 shrink-0 text-primary" />{name}</span>
                    <span className="col-start-2 text-xs text-muted-foreground sm:col-start-auto">{proximity}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── Pre-book card ── */}
          <section className="my-14 grid gap-8 rounded-[1.5rem] border border-border bg-card p-6 sm:my-20 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-10 lg:p-10 xl:p-12">
            <div>
              <p className="site-label">{bookingOpen ? 'No payment now' : 'Opening next'}</p>
              <h2 className="mt-5 max-w-3xl font-sans text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">{bookingOpen ? 'Request a private or shared session.' : 'Interested in this experience?'}</h2>
              <p className="mt-4 max-w-xl leading-7 text-muted-foreground">
                {!bookingOpen
                  ? 'Booking is not open yet. Ask us to let you know when dates and prices are ready.'
                  : editorial
                  ? `Spend an afternoon with ${editorial.hostName}. ${editorial.price} · ${editorial.duration} · up to ${editorial.maxGuests} guests. Our team will confirm availability with you on WhatsApp.`
                  : 'Share your preferred date and group size. Our team will confirm the price, group size, availability and practical details with you on WhatsApp before payment.'}
              </p>
              {bookingOpen && <p className="mt-3 text-sm text-muted-foreground">Cancellation terms vary by host and experience. Review the applicable policy before payment.</p>}
            </div>
            {preBookTrigger('Request a date', 'w-full bg-brand text-white hover:bg-brand-hover focus-visible:border-brand focus-visible:ring-brand/30 lg:w-auto')}
          </section>

          {/* ── What stays with you (conversation card, below no-payment box) ── */}
          {editorial && (
            <section className="border-b border-border py-14 sm:py-20">
              <div className="mx-auto max-w-3xl text-center">
                <p className="site-label">What stays with you</p>
                <span className="mt-4 block font-sans text-7xl leading-none text-brand/55" aria-hidden="true">&ldquo;</span>
                <blockquote className="-mt-6 font-sans text-3xl font-semibold leading-[1.2] tracking-[-0.03em] text-foreground sm:text-4xl">
                  {editorial.conversationCard}
                </blockquote>
                <p className="mt-8 text-sm font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                  {editorial.hostName}
                </p>
              </div>
            </section>
          )}

          <Link
            href={nextEditorial ? getCanonicalExperiencePath(nextSlug) : getHomepageExperiencePath(nextSlug)}
            data-analytics-event="experience_selected"
            data-analytics-experience-slug={nextSlug}
            data-analytics-experience-name={nextName}
            data-analytics-selection-source="experience_next"
            className="group mb-8 flex items-end justify-between gap-6 border-y border-border py-8 sm:mb-10 sm:gap-8 sm:py-9"
          >
            <div>
              <p className="site-label">Explore next</p>
              <h2 className="mt-4 whitespace-pre-line font-sans text-3xl font-semibold transition group-hover:text-brand sm:text-4xl">{nextName}</h2>
            </div>
            <ArrowRight className="mb-1 size-5 text-muted-foreground" />
          </Link>

          {related.length > 0 && (
            <section aria-label="More experiences" className="mb-16 sm:mb-20">
              <p className="site-label">More in {destination}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {related.map((item) => (
                  <Link
                    key={item.slug}
                    href={getCanonicalExperiencePath(item.slug)}
                    data-analytics-event="experience_selected"
                    data-analytics-experience-slug={item.slug}
                    data-analytics-experience-name={item.name}
                    data-analytics-selection-source="experience_related"
                    className="group rounded-2xl border border-border bg-card p-5 transition hover:border-foreground/25"
                  >
                    <p className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">{item.location}</p>
                    <p className="mt-2 font-semibold leading-snug transition group-hover:text-brand">{item.name}</p>
                    <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground group-hover:text-foreground">
                      View experience <ArrowRight className="size-3.5" />
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>

      </div>

      <Footer />
      <div className="h-16 bg-primary sm:hidden" aria-hidden="true" />

      <div className="fixed inset-x-4 bottom-4 z-30 sm:hidden">
        {preBookTrigger('Request a date', 'w-full bg-brand text-white shadow-2xl shadow-primary/20 hover:bg-brand-hover focus-visible:border-brand focus-visible:ring-brand/30')}
      </div>
    </div>
  )
}

function Fact({ icon: Icon, label, value }: { icon: typeof MapPin; label: string; value: string }) {
  return (
    <div className="bg-card p-5">
      <Icon className="size-4 text-brand" />
      <p className="mt-5 text-[0.55rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{label}</p>
      <p className="mt-2 text-sm text-foreground/75">{value}</p>
    </div>
  )
}

function DimensionMeter({ score }: { score: 'full' | 'partial' }) {
  const filled = score === 'full' ? 3 : 2
  return (
    <div className="flex items-center gap-1.5" aria-label={score === 'full' ? 'Full' : 'Partial'}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`h-1 w-8 rounded-full ${i < filled ? 'bg-brand' : 'bg-foreground/15'}`}
        />
      ))}
    </div>
  )
}

function MissingExperience() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5 text-center text-foreground">
      <div>
        <p className="site-label">Experience not found</p>
        <h1 className="mt-6 font-sans text-5xl">This story is not available.</h1>
        <Link href="/#experiences" className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="size-4" />Return to discover</Link>
      </div>
    </div>
  )
}
